import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import { BreakdownBarChartCard } from "@/components/BreakdownBarChartCard.tsx";
import { DataTable } from "@/components/data-table/data-table.tsx";
import { PageTitle } from "@/components/ui/headings";
import { ToolbarOptions } from "@/types/data-table/ToolbarOptions.ts";
import { Mentor } from "@/types/Mentor.ts";
import { queryKeys } from "@/services/queryKeyFactory.ts";
import {
    getMentors,
    getMentorBreakdownStats,
    type MentorBreakdownStats,
} from "@/services/mentorService.ts";
import { getMentorsColumns } from "@/pages/mentors/mentors-columns.tsx";
import MentorForm from "@/pages/mentors/MentorForm.tsx";
import { useTranslation } from "react-i18next";

const MentorsPage = () => {
    const { t } = useTranslation();
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const [selectedMentor, setSelectedMentor] = useState<Mentor | null>(null);
    const { data: mentors, isFetching } = useQuery({
        queryKey: queryKeys.fetchMentors.all,
        queryFn: () => getMentors(),
        initialData: [],
    });

    const { data: breakdown, isFetching: breakdownFetching } = useQuery<MentorBreakdownStats>({
        queryKey: queryKeys.fetchMentors.breakdown,
        queryFn: () => getMentorBreakdownStats(),
    });

    const onEdit = useCallback((mentor: Mentor) => {
        setSelectedMentor(mentor);
        setIsDialogOpen(true);
    }, []);

    const toolbarOptions: ToolbarOptions = {
        filters: [
            { column: "profileCompletion", title: "profileCompletion" },
            { column: "careerFocus", title: "careerFocus" },
            { column: "experience", title: "experience" },
            { column: "disciplines", title: "disciplines" },
            { column: "researchExpertises", title: "researchExpertises" },
            { column: "languages", title: "languages" },
            { column: "socioeconomicDisadvantages", title: "socioeconomicDisadvantages" },
            { column: "races", title: "races" },
            { column: "indigeneities", title: "indigeneities" },
            { column: "disabilities", title: "disabilities" },
            { column: "gender", title: "gender" },
            { column: "sexualOrientation", title: "sexualOrientation" },
        ],
        searchableColumn: "name",
        sendEmailColumn: "email",
    };

    const hiddenColumns: string[] = [
        "profileCompletion",
        "researchExpertises",
        "socioeconomicDisadvantages",
        "isMale",
        "isFemale",
        "gender",
        "sexualOrientation",
        "disabilities",
    ];

    const columns = useMemo(
        () => getMentorsColumns({ onEdit }),
        [onEdit]
    );

    const showCharts = breakdown && !breakdownFetching;
    const hasPrevSeason =
        showCharts &&
        (breakdown.byDiscipline.some((b) => b.prevCount !== undefined) ||
            breakdown.byResearchExpertise.some((b) => b.prevCount !== undefined) ||
            breakdown.byExperience.some((b) => b.prevCount !== undefined));
    const footerSubText = showCharts
        ? hasPrevSeason
            ? t("mentors.charts.footerCompared")
            : t("mentors.charts.footerNoPrev")
        : undefined;

    return (
        <div className="container space-y-6 pb-12 pt-8">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="space-y-1">
                    <PageTitle>{t("navbar.mentors")}</PageTitle>
                    <p className="text-sm text-text-2">{t("mentors.pageDesc")}</p>
                </div>
                <div className="flex-shrink-0">
                    <MentorForm
                        isOpen={isDialogOpen}
                        mentor={selectedMentor}
                        onOpenChange={(value) => {
                            setIsDialogOpen(value);
                            if (!value) setSelectedMentor(null);
                        }}
                    />
                </div>
            </div>

            <div className="flex flex-col gap-6">
                {showCharts && (
                    <div className="order-2 grid gap-4 md:order-1 md:grid-cols-3">
                        <BreakdownBarChartCard
                            title={t("mentors.charts.discipline")}
                            description={t("mentors.charts.disciplineDesc")}
                            data={breakdown.byDiscipline}
                            totalCurrent={breakdown.totalCurrent}
                            totalPrev={breakdown.totalPrev}
                            footerText={t("mentors.charts.footer")}
                            footerSubText={footerSubText}
                            tooltipKeyPrefix="mentors.charts"
                        />
                        <BreakdownBarChartCard
                            title={t("mentors.charts.researchExpertise")}
                            description={t("mentors.charts.researchExpertiseDesc")}
                            data={breakdown.byResearchExpertise}
                            totalCurrent={breakdown.totalCurrent}
                            totalPrev={breakdown.totalPrev}
                            footerText={t("mentors.charts.footer")}
                            footerSubText={footerSubText}
                            tooltipKeyPrefix="mentors.charts"
                        />
                        <BreakdownBarChartCard
                            title={t("mentors.charts.experience")}
                            description={t("mentors.charts.experienceDesc")}
                            data={breakdown.byExperience}
                            totalCurrent={breakdown.totalCurrent}
                            totalPrev={breakdown.totalPrev}
                            footerText={t("mentors.charts.footer")}
                            footerSubText={footerSubText}
                            tooltipKeyPrefix="mentors.charts"
                        />
                    </div>
                )}

                <div className="order-1 md:order-2">
                    {isFetching && (
                        <div className="flex flex-col space-y-3">
                            <div className="space-y-2">
                                <Skeleton className="h-8 w-full" />
                                <Skeleton className="h-8 w-full" />
                                <Skeleton className="h-8 w-full" />
                                <Skeleton className="h-8 w-full" />
                                <Skeleton className="h-8 w-full" />
                                <Skeleton className="h-8 w-full" />
                            </div>
                        </div>
                    )}
                    {!isFetching && (
                        <DataTable
                            data={mentors}
                            columns={columns}
                            id="mentors"
                            hiddenColumns={hiddenColumns}
                            toolbarOptions={toolbarOptions}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default MentorsPage;
